import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Reveal } from '../../components/shared/reveal/reveal';
import { getPost, BlogApiError } from '../../components/shared/blog/blog-api';
import { formatDate } from '../../components/shared/blog/format-date';
import { isBlogConfigured } from '../../config';
import type { Post } from '../../components/shared/blog/contracts/post';

type LoadState = 'loading' | 'loaded' | 'not-found' | 'error';

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [state, setState] = useState<LoadState>('loading');

  useEffect(() => {
    if (!slug) return;
    if (!isBlogConfigured()) {
      setState('not-found');
      return;
    }
    const controller = new AbortController();
    setState('loading');
    getPost(slug, controller.signal)
      .then((p) => {
        setPost(p);
        setState('loaded');
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setState(err instanceof BlogApiError && err.status === 404 ? 'not-found' : 'error');
      });
    return () => controller.abort();
  }, [slug]);

  // Keep the document title in sync for sharing / SEO.
  useEffect(() => {
    document.title = post ? `${post.title} — Nick Gardner` : 'Blog — Nick Gardner';
    return () => {
      document.title = 'Nick Gardner';
    };
  }, [post]);

  return (
    <main id="main" className="mx-auto max-w-content px-5 py-16">
      <Reveal>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 font-mono text-sm text-muted transition-colors hover:text-accent"
        >
          <span aria-hidden>←</span> back to blog
        </Link>
      </Reveal>

      {state === 'loading' && <PostSkeleton />}
      {state === 'not-found' && <PostNotFound />}
      {state === 'error' && <PostError />}

      {state === 'loaded' && post && (
        <article className="mx-auto mt-8 max-w-prose">
          <Reveal>
            <header>
              {post.publishedAt && (
                <p className="font-mono text-sm tabular-nums text-muted">
                  {formatDate(post.publishedAt)}
                </p>
              )}
              <h1 className="mt-2 font-mono text-3xl font-bold leading-tight tracking-tight text-fg sm:text-4xl">
                {post.title}
              </h1>
              {post.tags && post.tags.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {post.tags.map((t) => (
                    <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="tag">
                      #{t}
                    </Link>
                  ))}
                </div>
              )}
            </header>
          </Reveal>

          <Reveal className="mt-8">
            <div className="prose-post">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
            </div>
          </Reveal>
        </article>
      )}
    </main>
  );
}

function PostSkeleton() {
  return (
    <div className="mx-auto mt-8 max-w-prose animate-pulse" aria-hidden>
      <div className="h-3 w-24 rounded bg-elevated" />
      <div className="mt-3 h-8 w-4/5 rounded bg-elevated" />
      <div className="mt-8 space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-4 w-full rounded bg-elevated" />
        ))}
      </div>
    </div>
  );
}

function PostNotFound() {
  return (
    <div className="terminal-card mx-auto mt-8 max-w-prose px-6 py-16 text-center">
      <p className="font-mono text-sm text-accent">{'// 404'}</p>
      <p className="mt-2 text-muted">That post doesn&apos;t exist (or isn&apos;t published).</p>
      <Link to="/blog" className="tag mt-6 inline-flex">
        browse all posts
      </Link>
    </div>
  );
}

function PostError() {
  return (
    <div className="terminal-card mx-auto mt-8 max-w-prose px-6 py-16 text-center" role="alert">
      <p className="font-mono text-sm text-accent">{'// error'}</p>
      <p className="mt-2 text-muted">Something went wrong loading this post.</p>
    </div>
  );
}
